import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { fetchServerSession, resolveAccessTokenValue } from '../lib/sessionAuth';

// Type for fetch options
type FetchOptions = RequestInit & {
  body?: any;
  skipTokenRefresh?: boolean; // Add flag to prevent refresh loops
  includeAccessTokenInBody?: boolean;
};

// Auth context type
interface AuthContext {
  userEmail: string | null;
  userName: string | null;
  accessToken: string | null;
  setUserInfo: (email: string | null, name: string | null, token: string | null) => void;
  setIsPremium?: (isPremium: boolean) => void;
  logout?: () => void;
}

// Track refresh attempts to prevent loops
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

const attachAuthorizationHeader = (
  options: FetchOptions,
  tokenValue: string | null | undefined
): string | null => {
  const token = resolveAccessTokenValue(tokenValue);
  if (!token) return null;

  options.headers = {
    ...options.headers,
    Authorization: `Bearer ${token}`,
  };
  return token;
};

const isPlainObject = (value: unknown): value is Record<string, any> =>
  Object.prototype.toString.call(value) === '[object Object]';

const parseBodyData = (body: unknown): unknown => {
  if (typeof body !== 'string') return body;
  try {
    return JSON.parse(body);
  } catch {
    return body;
  }
};

/**
 * Attempts to refresh the auth token.
 * Returns the newly issued access token when successful.
 */
export const refreshAuthToken = async (authContext: AuthContext): Promise<string | null> => {
  // If already refreshing, return the existing promise to prevent multiple refreshes.
  if (isRefreshing && refreshPromise) {
    console.log('🔄 Token refresh already in progress, using existing promise');
    return refreshPromise;
  }

  console.log('🔑 Starting token refresh process...');

  // Set refreshing flag and create a new promise.
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const session = await fetchServerSession({ refresh: true });
      const refreshedToken = resolveAccessTokenValue(session?.accessToken || null);
      if (!refreshedToken) {
        console.error('❌ Server session refresh returned no access token');
        return null;
      }

      const resolvedEmail = session?.userEmail ?? authContext.userEmail;
      const resolvedName = session?.userName ?? authContext.userName;
      authContext.setUserInfo(resolvedEmail, resolvedName, refreshedToken);

      if (typeof authContext.setIsPremium === 'function') {
        authContext.setIsPremium(Boolean(session?.isPremium));
      }

      console.log('✅ Refreshed access token from server-managed session');
      return refreshedToken;
    } catch (error) {
      console.error('❌ Token refresh error:', error);
      return null;
    } finally {
      // Reset refreshing state.
      isRefreshing = false;
      refreshPromise = null;
      console.log('🔄 Token refresh process completed, reset refresh state');
    }
  })();

  return refreshPromise;
};

const executeAuthenticatedFetch = async (
  url: string,
  options: FetchOptions,
  authContext: AuthContext,
  onSessionExpired?: () => void
): Promise<Response> => {
  console.log(`🌐 Making authenticated request to: ${url}`);

  const modifiedOptions: FetchOptions = { ...options };
  modifiedOptions.headers = {
    ...modifiedOptions.headers,
  };

  let activeToken = attachAuthorizationHeader(modifiedOptions, authContext.accessToken);
  if (activeToken) {
    console.log('🔑 Added Authorization header with Bearer token');
  }

  let bodyData = parseBodyData(options.body);
  if (isPlainObject(bodyData)) {
    modifiedOptions.headers = {
      'Content-Type': 'application/json',
      ...modifiedOptions.headers,
    };

    if (
      options.includeAccessTokenInBody !== false &&
      activeToken &&
      (bodyData.accessToken === null || bodyData.accessToken === undefined)
    ) {
      bodyData = { ...bodyData, accessToken: activeToken };
    }

    modifiedOptions.body = JSON.stringify(bodyData);
  } else if (typeof options.body !== 'undefined') {
    modifiedOptions.body = options.body;
  }

  let response = await fetch(url, modifiedOptions);
  console.log(`📡 Response received with status: ${response.status}`);

  // Handle both 401 (Unauthorized) and 403 (Forbidden) status codes.
  if (
    (response.status === 401 || response.status === 403) &&
    authContext.userEmail &&
    !options.skipTokenRefresh
  ) {
    console.log(`⚠️ Request returned ${response.status}, attempting token refresh...`);
    const refreshedToken = await refreshAuthToken(authContext);

    if (refreshedToken) {
      activeToken = attachAuthorizationHeader(modifiedOptions, refreshedToken);
      if (activeToken && isPlainObject(bodyData) && options.includeAccessTokenInBody !== false) {
        bodyData = { ...bodyData, accessToken: activeToken };
        modifiedOptions.body = JSON.stringify(bodyData);
      }

      modifiedOptions.skipTokenRefresh = true;
      response = await fetch(url, modifiedOptions);
      console.log(`📡 Retry response received with status: ${response.status}`);
    } else {
      console.error('❌ Token refresh failed');
      onSessionExpired?.();
    }
  } else if (response.status === 401 || response.status === 403) {
    if (!authContext.userEmail) {
      console.log('⚠️ Auth error received but no user email in context - user likely not logged in');
    } else if (options.skipTokenRefresh) {
      console.log('⚠️ Auth error received but skipTokenRefresh flag is set - preventing refresh loop');
    }
  }

  return response;
};

/**
 * Hook that provides an authenticated fetch function.
 * This should be used inside functional components.
 */
export const useAuthenticatedFetch = () => {
  const auth = useAuth();
  const navigate = useNavigate();

  return async (url: string, options: FetchOptions = {}) =>
    executeAuthenticatedFetch(url, options, auth, () => {
      auth.logout?.();
      navigate('/login', {
        state: {
          redirectAfterLogin: window.location.pathname,
          message: 'Your session expired. Please log in again.',
        },
      });
    });
};

/**
 * A standalone version of authenticatedFetch for use outside React components.
 * Note: This cannot use React hooks like useNavigate, so it won't handle redirects automatically.
 */
export const authenticatedFetchStandalone = async (
  url: string,
  options: FetchOptions = {},
  authContext: AuthContext,
  onSessionExpired?: () => void
) => executeAuthenticatedFetch(url, options, authContext, onSessionExpired);
